import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Wallet, CheckCircle2 } from "lucide-react";

export default function CustomerRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/customer/register", {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        password: form.password,
      });
      setDone(true);
      toast.success("Registration submitted");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/9800323/pexels-photo-9800323.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-950/40 to-slate-950/90" />
        <div className="relative p-10 z-10">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <span className="font-display text-lg font-bold tracking-tight">ThankF</span>
          </div>
        </div>
        <div className="relative p-10 z-10 max-w-md">
          <p className="label-overline text-slate-300">Create an account</p>
          <h1 className="font-display text-5xl font-extrabold leading-tight mt-3">
            Register once, <br />
            wait for approval.
          </h1>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Fill in your details below. An administrator will review and activate
            your account. You'll be able to sign in once approved.
          </p>
        </div>
        <div className="relative p-10 z-10 label-overline text-slate-400">
          A THANKF PRODUCT — SECURE CUSTOMER WALLET
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-grid">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <span className="font-display text-lg font-bold">ThankF</span>
          </div>

          {done ? (
            <div
              data-testid="register-success-card"
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center"
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-700 mx-auto" />
              <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">
                Registration submitted
              </h2>
              <p className="text-slate-600 mt-2 text-sm">
                Your account is pending admin approval. You'll be able to sign in
                once an administrator activates it.
              </p>
              <div className="mt-6">
                <Button
                  data-testid="go-to-login-btn"
                  onClick={() => navigate("/portal")}
                  className="bg-slate-900 hover:bg-slate-800 gap-2"
                >
                  Go to sign in <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="label-overline text-slate-500">New customer</p>
              <h2 className="font-display text-4xl font-extrabold mt-2 text-slate-900">
                Create your account
              </h2>
              <p className="text-slate-600 mt-2">
                Fill out the form — an admin will approve your account shortly.
              </p>

              <form
                onSubmit={onSubmit}
                className="mt-10 space-y-5"
                data-testid="customer-register-form"
              >
                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="label-overline text-slate-700">
                    Full name
                  </Label>
                  <Input
                    id="reg-name"
                    data-testid="register-name-input"
                    value={form.name}
                    onChange={set("name")}
                    className="h-12 text-base"
                    required
                    maxLength={80}
                    placeholder="e.g. Ravi Kumar"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-mobile" className="label-overline text-slate-700">
                    Mobile number
                  </Label>
                  <Input
                    id="reg-mobile"
                    data-testid="register-mobile-input"
                    inputMode="numeric"
                    placeholder="9876543210"
                    value={form.mobile}
                    onChange={(e) =>
                      setForm({ ...form, mobile: e.target.value.replace(/[^0-9+]/g, "") })
                    }
                    className="font-mono-num h-12 text-base"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-pwd" className="label-overline text-slate-700">
                    Password (min 4 chars)
                  </Label>
                  <Input
                    id="reg-pwd"
                    data-testid="register-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={set("password")}
                    className="h-12 text-base"
                    required
                    minLength={4}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={submitting}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md gap-2"
                >
                  {submitting ? "Submitting…" : "Create account"}
                  {!submitting && <ArrowRight className="h-4 w-4" />}
                </Button>
              </form>

              <div className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-500">Already registered?</p>
                <Link
                  to="/portal"
                  data-testid="goto-login-link"
                  className="text-sm font-medium text-slate-900 hover:underline underline-offset-4"
                >
                  Sign in →
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
